import { useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import {
  useCreateFamilyMember,
  useDeleteFamilyMember,
  useEmployeeFamily,
  useGenderOptions,
  useRelationshipTypes,
  useUpdateFamilyMember,
} from "../hooks";
import type { FamilyMember, FamilyMemberFormValues } from "../types";
import { useAuth } from "#/context/AuthProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { useToast } from "#/components/common/ToastProvider";

function getErrorMessage(error: unknown) {
  const anyError = error as any;

  const validationErrors = anyError?.response?.data?.validationErrors;
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    return validationErrors
      .map((item: any) => `${item.field}: ${item.message}`)
      .join(", ");
  }

  return (
    anyError?.response?.data?.message ||
    anyError?.response?.data?.details ||
    anyError?.message ||
    "Something went wrong. Please try again."
  );
}

function fmtDate(val?: string | null) {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime())
    ? val
    : d.toLocaleDateString("en-KE", { day: "2-digit", month: "short", year: "numeric" });
}

const EMPTY_FORM: FamilyMemberFormValues = {
  relationshipTypeId: "",
  fullName: "",
  dateOfBirth: "",
  genderId: "",
  dependent: true,
  nextOfKin: false,
  notes: "",
};

function toFormValues(member: FamilyMember): FamilyMemberFormValues {
  return {
    relationshipTypeId: member.relationshipTypeId,
    fullName: member.fullName,
    dateOfBirth: member.dateOfBirth ?? "",
    genderId: member.genderId ?? "",
    dependent: member.dependent,
    nextOfKin: member.nextOfKin,
    notes: member.notes ?? "",
  };
}

function FamilyMemberFormModal({
  title,
  initialValues,
  relationshipTypes,
  genderOptions,
  saving,
  onClose,
  onSave,
}: {
  title: string;
  initialValues: FamilyMemberFormValues;
  relationshipTypes: { id: number; code: string; name: string }[];
  genderOptions: { id: number; code: string; name: string }[];
  saving: boolean;
  onClose: () => void;
  onSave: (values: FamilyMemberFormValues) => void;
}) {
  const [values, setValues] = useState<FamilyMemberFormValues>(initialValues);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof FamilyMemberFormValues>(key: K, value: FamilyMemberFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit() {
    if (!values.relationshipTypeId) {
      setError("Select a relationship.");
      return;
    }
    if (!values.fullName.trim()) {
      setError("Full name is required.");
      return;
    }
    setError(null);
    onSave(values);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-base font-black text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="grid grid-cols-2 gap-4">
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Relationship *
              </span>
              <select
                className="block w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
                value={values.relationshipTypeId}
                onChange={(e) =>
                  update(
                    "relationshipTypeId",
                    e.target.value ? Number(e.target.value) : "",
                  )
                }
              >
                <option value="">Select…</option>
                {relationshipTypes.map((rt) => (
                  <option key={rt.id} value={rt.id}>
                    {rt.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Gender
              </span>
              <select
                className="block w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
                value={values.genderId}
                onChange={(e) =>
                  update("genderId", e.target.value ? Number(e.target.value) : "")
                }
              >
                <option value="">Not specified</option>
                {genderOptions.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Full Name *
            </span>
            <input
              type="text"
              className="block w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500"
              value={values.fullName}
              onChange={(e) => update("fullName", e.target.value)}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Date of Birth
            </span>
            <input
              type="date"
              className="block w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500"
              value={values.dateOfBirth}
              onChange={(e) => update("dateOfBirth", e.target.value)}
            />
          </label>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={values.dependent}
                onChange={(e) => update("dependent", e.target.checked)}
              />
              Dependent
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={values.nextOfKin}
                onChange={(e) => update("nextOfKin", e.target.checked)}
              />
              Next of Kin
            </label>
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Notes
            </span>
            <textarea
              rows={2}
              className="block w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500"
              value={values.notes}
              onChange={(e) => update("notes", e.target.value)}
            />
          </label>

          {error ? (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function EmployeeFamilyPanel({ employeeId }: { employeeId: number }) {
  const { hasRole, hasAnyPermission } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();

  const canManage =
    hasRole("SUPER_ADMIN") || hasAnyPermission(["HR_EMPLOYEE_EDIT", "HR_EMPLOYEE_MANAGE"]);

  const { data: members, isLoading, error } = useEmployeeFamily(employeeId);
  const { data: relationshipTypes } = useRelationshipTypes();
  const { data: genderOptions } = useGenderOptions();

  const createMutation = useCreateFamilyMember(employeeId);
  const updateMutation = useUpdateFamilyMember(employeeId);
  const deleteMutation = useDeleteFamilyMember(employeeId);

  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);

  const saving = createMutation.isPending || updateMutation.isPending;

  function openCreate() {
    setEditingMember(null);
    setModalMode("create");
  }

  function openEdit(member: FamilyMember) {
    setEditingMember(member);
    setModalMode("edit");
  }

  function closeModal() {
    setModalMode(null);
    setEditingMember(null);
  }

  async function handleSave(values: FamilyMemberFormValues) {
    try {
      if (modalMode === "edit" && editingMember) {
        await updateMutation.mutateAsync({ familyId: editingMember.id, payload: values });
        toast.success("Family member updated.");
      } else {
        await createMutation.mutateAsync(values);
        toast.success("Family member added.");
      }
      closeModal();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleDelete(member: FamilyMember) {
    if (!(await confirm(`Remove ${member.fullName} from this employee's family records?`))) return;
    try {
      await deleteMutation.mutateAsync(member.id);
      toast.success("Family member removed.");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Spouse, children and other dependents recorded for this employee.
        </p>
        {canManage && (
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
          >
            <Plus className="h-3.5 w-3.5" /> Add Family Member
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm text-slate-400">Loading…</p>}
      {error && (
        <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">
          Failed to load family members.
        </p>
      )}

      {!isLoading && !error && (members?.length ?? 0) === 0 && (
        <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400">
          No family members recorded yet.
        </p>
      )}

      {!isLoading && (members?.length ?? 0) > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b text-xs font-semibold uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Relationship</th>
                <th className="py-2 pr-3">Date of Birth</th>
                <th className="py-2 pr-3">Gender</th>
                <th className="py-2 pr-3">Flags</th>
                {canManage && <th className="py-2 pr-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {members!.map((member) => (
                <tr key={member.id} className="border-b last:border-0">
                  <td className="py-2.5 pr-3 font-medium text-slate-800">{member.fullName}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{member.relationshipTypeName}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{fmtDate(member.dateOfBirth)}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{member.genderName || "—"}</td>
                  <td className="py-2.5 pr-3">
                    <div className="flex flex-wrap gap-1.5">
                      {member.dependent && (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                          Dependent
                        </span>
                      )}
                      {member.nextOfKin && (
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                          Next of Kin
                        </span>
                      )}
                    </div>
                  </td>
                  {canManage && (
                    <td className="py-2.5 pr-3">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => openEdit(member)}
                          className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(member)}
                          className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalMode && (
        <FamilyMemberFormModal
          title={modalMode === "edit" ? "Edit Family Member" : "Add Family Member"}
          initialValues={editingMember ? toFormValues(editingMember) : EMPTY_FORM}
          relationshipTypes={relationshipTypes ?? []}
          genderOptions={genderOptions ?? []}
          saving={saving}
          onClose={closeModal}
          onSave={handleSave}
        />
      )}
    </>
  );
}
